import { call, put, select, take, takeEvery } from 'redux-saga/effects';
import {
  DoRequestResponse,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  VorbelegungPayload,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  mapVorbelegungdaten,
  RsVorbelegungdaten,
  VermietetesObjekt,
  Vorbelegungdaten,
} from '@ps20-frontend/frontend-models';
import { RsVorvertragsdaten, SpartenType, VertragsdatenRequestModel } from '@ps20/bff-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { getBebauteObjekte, getUnbebauteObjekte } from '@ps20-frontend/reducers';
import { IAppState } from '../appState';
import { RsVermieterActions } from '../rs-vermietung-actions.service';

export enum VorbelegungRsVmRequestEventsEnum {
  VORBELEGUNG_RS_VM_REQUEST_SUCCESSFULL = 'VORBELEGUNG_RS_VM_REQUEST_SUCCESSFULL',
  VORBELEGUNG_RS_VM_REQUEST_FAILED = 'VORBELEGUNG_RS_VM_REQUEST_FAILED',
}

export function* watchVorbelegungRequested() {
  yield takeEvery(
    GlobalActions.VORBELEGUNG_REQUESTED,
    safeSaga(vorbelegungSaga, App.RS_VERMIETUNG),
  );
  yield takeEvery(
    VorbelegungRsVmRequestEventsEnum.VORBELEGUNG_RS_VM_REQUEST_SUCCESSFULL,
    safeSaga(successCallback, App.RS_VERMIETUNG),
  );
  yield takeEvery(
    VorbelegungRsVmRequestEventsEnum.VORBELEGUNG_RS_VM_REQUEST_FAILED,
    safeSaga(errorCallback, App.RS_VERMIETUNG),
  );
}

const globalActions = new GlobalActions();

export function* vorbelegungSaga(action: GlobalAction<VorbelegungPayload>) {
  if (action.payload.appId !== App.RS_VERMIETUNG) {
    return;
  }

  if (action.payload?.vorhandeneVorbelegungdaten) {
    yield* rsVmDatenVorbelegen(action.payload.vorhandeneVorbelegungdaten);
  } else {
    const globalPut = GlobalPut.getInstance();
    const vertragsdatenRequestModel: VertragsdatenRequestModel = {
      spLaufnummer: action.payload.spLaufnummer,
      vertragsnummer: action.payload.vertragsnummer,
    };
    yield call(
      [globalPut, globalPut.put],
      globalActions.doRequest({
        appId: App.RS_VERMIETUNG,
        body: vertragsdatenRequestModel,
        successEvent: VorbelegungRsVmRequestEventsEnum.VORBELEGUNG_RS_VM_REQUEST_SUCCESSFULL,
        errorEvent: VorbelegungRsVmRequestEventsEnum.VORBELEGUNG_RS_VM_REQUEST_FAILED,
        url: 'privatschutz/api/vertragsdaten',
      }),
    );
  }
}

export function* successCallback(successAction: DoRequestResponse<RsVorvertragsdaten>) {
  const rsVorbelegungdaten: RsVorbelegungdaten = mapVorbelegungdaten(
    SpartenType.RECHTSSCHUTZ_VERMIETUNG,
    successAction.payload,
  ) as RsVorbelegungdaten;
  yield* rsVmDatenVorbelegen(rsVorbelegungdaten);
}

const rsVermieterActions = new RsVermieterActions();

export function* rsVmDatenVorbelegen(vorbelegungdaten: Vorbelegungdaten) {
  const state: IAppState = yield select();
  const rsVorbelegungdaten: RsVorbelegungdaten = vorbelegungdaten as RsVorbelegungdaten;
  const globalPut = GlobalPut.getInstance();
  if (vorbelegungdaten.internerVertragsbezugs) {
    yield put(
      globalActions.internerVertragsbezugReceived(
        vorbelegungdaten.internerVertragsbezugs,
        App.RS_VERMIETUNG,
        state.angebotsId,
      ),
    );
  }
  yield put(rsVermieterActions.familienstandChanged(rsVorbelegungdaten.familienstand));
  // Vorbeleung Vermietete Objekte
  const vermieteteObjekte: Array<VermietetesObjekt> = [];
  if (rsVorbelegungdaten.bebauteGebaeude?.length > 0) {
    getBebauteObjekte(rsVorbelegungdaten, vermieteteObjekte);
  }
  if (rsVorbelegungdaten.unbebauteGrundstuecke?.length > 0) {
    getUnbebauteObjekte(rsVorbelegungdaten, vermieteteObjekte);
  }
  if (vermieteteObjekte.length > 0) {
    yield put(rsVermieterActions.overwriteVermieteteObjekte(vermieteteObjekte));
  }
  if (!state.produkt?.produktlinien) {
    yield take(GlobalActions.PRODUKT_RECEIVED);
  }

  if (!state.vertragsoptionen?.beibehalten) {
    yield call(
      [globalPut, globalPut.put],
      globalActions.navigationRequested({
        appId: App.RS_VERMIETUNG,
        subroute: '/eingabe',
      }),
    );
  } else {
    yield call(
      [globalPut, globalPut.put],
      globalActions.vorbelegungForBeibehaltungRequested(rsVorbelegungdaten),
    );
  }
  yield call(
    [globalPut, globalPut.put],
    globalActions.vorbelegungFinished({ appId: App.RS_VERMIETUNG }),
  );
}

export function* errorCallback() {
  const globalPut = GlobalPut.getInstance();
  yield call(
    [globalPut, globalPut.put],
    globalActions.vorbelegungFinished({ appId: App.RS_VERMIETUNG }),
  );
  yield call(
    [globalPut, globalPut.put],
    globalActions.navigationRequested({
      appId: App.RS_VERMIETUNG,
      subroute: '/eingabe',
    }),
  );
}
